import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Sparkles, KeyRound, Globe, Bot, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = API_BASE_URL;

const AIBotSettings = () => {
  const { user } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const [settings, setSettings] = useState({
    enabled: true,
    model: 'gpt-5.2',
    language: 'en',
    tone: 'friendly',
    system_prompt: '',
    business_info: '',
    openai_api_key: '',
    openai_api_key_set: false
  });
  const [testMessage, setTestMessage] = useState('Hi, what are your business hours?');
  const [testResponse, setTestResponse] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchRestaurants(); }, []);
  useEffect(() => {
    if (selectedRestaurantId) fetchSettings(selectedRestaurantId);
  }, [selectedRestaurantId]);

  const fetchRestaurants = async () => {
    try {
      const { data } = await axios.get(`${API}/restaurants`, { withCredentials: true });
      setRestaurants(data);
      if (data.length > 0) setSelectedRestaurantId(data[0].id);
    } catch (error) { console.error(error); }
  };

  const fetchSettings = async (id) => {
    try {
      const { data } = await axios.get(`${API}/restaurants/${id}/ai-settings`, { withCredentials: true });
      setSettings({ ...data, openai_api_key: '' });
    } catch (error) { console.error(error); }
  };

  const handleSave = async () => {
    if (!selectedRestaurantId) return;
    setSaving(true);
    try {
      const payload = { ...settings };
      if (!payload.openai_api_key) delete payload.openai_api_key;  // Don't overwrite with empty
      delete payload.openai_api_key_set;
      await axios.patch(`${API}/restaurants/${selectedRestaurantId}/ai-settings`, payload, { withCredentials: true });
      toast.success('AI settings saved');
      fetchSettings(selectedRestaurantId);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResponse('');
    try {
      const { data } = await axios.post(`${API}/whatsapp/ai-suggest`, {
        message: testMessage,
        restaurant_id: selectedRestaurantId
      }, { withCredentials: true });
      setTestResponse(data.suggestion);
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-bot-settings-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E57200' }}>
          <Bot className="h-7 w-7 text-white" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
            AI Bot Settings
          </h1>
          <p className="text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Configure your restaurant's AI assistant prompt and behavior
          </p>
        </div>
      </div>

      {/* Restaurant Selector */}
      {restaurants.length > 1 && (
        <Card className="border-border/40">
          <CardContent className="pt-6">
            <Label>Select Restaurant</Label>
            <Select value={selectedRestaurantId || ''} onValueChange={setSelectedRestaurantId}>
              <SelectTrigger data-testid="restaurant-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                {restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {!selectedRestaurantId ? (
        <Card className="border-border/40">
          <CardContent className="py-12 text-center text-muted-foreground">
            No restaurants available. Create one first.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Sparkles className="h-5 w-5" style={{ color: '#E57200' }} />
                  Bot Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Switch checked={settings.enabled} onCheckedChange={(c) => setSettings({ ...settings, enabled: c })} data-testid="bot-enabled" />
                  <Label>Enable AI Bot</Label>
                  {settings.enabled && <Badge style={{ backgroundColor: '#10B981', color: 'white' }} className="ms-2">Active</Badge>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Model</Label>
                    <Select value={settings.model} onValueChange={(v) => setSettings({ ...settings, model: v })}>
                      <SelectTrigger data-testid="bot-model"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gpt-5.2">GPT-5.2 (Latest)</SelectItem>
                        <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                        <SelectItem value="gpt-4o-mini">GPT-4o Mini (Cheaper)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Bot Language</Label>
                    <Select value={settings.language} onValueChange={(v) => setSettings({ ...settings, language: v })}>
                      <SelectTrigger data-testid="bot-language"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="ar">Arabic (العربية)</SelectItem>
                        <SelectItem value="ms">Bahasa Melayu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Response Tone</Label>
                    <Select value={settings.tone} onValueChange={(v) => setSettings({ ...settings, tone: v })}>
                      <SelectTrigger data-testid="bot-tone"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <MessageSquare className="h-5 w-5" style={{ color: '#E57200' }} />
                  System Prompt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  value={settings.system_prompt}
                  onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
                  rows={8}
                  placeholder="You are a friendly assistant for..."
                  className="font-mono text-sm"
                  data-testid="system-prompt"
                />
                <p className="text-xs text-muted-foreground">This is the master instruction your AI bot follows. Customize how it greets customers, handles orders, and answers questions.</p>
              </CardContent>
            </Card>

            <Card className="border-border/40">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Restaurant Information (Auto-Injected)</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={settings.business_info}
                  onChange={(e) => setSettings({ ...settings, business_info: e.target.value })}
                  rows={4}
                  placeholder="Name, hours, address, specialties..."
                  data-testid="business-info"
                />
                <p className="text-xs text-muted-foreground mt-2">Added to every AI conversation so the bot knows your business details.</p>
              </CardContent>
            </Card>

            <Card className="border-border/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <KeyRound className="h-5 w-5" style={{ color: '#E57200' }} />
                  OpenAI API Key (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Input
                  type="password"
                  placeholder={settings.openai_api_key_set ? "•••••••• (key set, enter to replace)" : "sk-..."}
                  value={settings.openai_api_key}
                  onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                  data-testid="openai-key"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to use the platform's shared key. Add your own for billing isolation.
                  {settings.openai_api_key_set && <Badge className="ms-2" style={{ backgroundColor: '#10B981', color: 'white' }}>Key Configured</Badge>}
                </p>
              </CardContent>
            </Card>

            <Button onClick={handleSave} disabled={saving} className="w-full" style={{ backgroundColor: '#E57200' }} data-testid="save-ai-settings">
              {saving ? 'Saving...' : 'Save AI Settings'}
            </Button>
          </div>

          {/* Test Console */}
          <div>
            <Card className="border-border/40 sticky top-20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Bot className="h-5 w-5" style={{ color: '#E57200' }} />
                  Test Console
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Label>Test Message</Label>
                <Textarea value={testMessage} onChange={(e) => setTestMessage(e.target.value)} rows={3} data-testid="test-message" />
                <Button onClick={handleTest} disabled={testing} className="w-full" style={{ backgroundColor: '#232D4B' }} data-testid="test-ai-btn">
                  {testing ? 'Testing...' : 'Send to Bot'}
                </Button>
                {testResponse && (
                  <div className="p-3 rounded-lg bg-[#25D366]/10 border border-[#25D366]/30">
                    <div className="text-xs font-bold text-[#25D366] mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>BOT REPLY</div>
                    <div className="text-sm whitespace-pre-wrap" style={{ fontFamily: 'Figtree, sans-serif' }} data-testid="test-response">{testResponse}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIBotSettings;
